import { AxiosResponse } from 'axios';
import apiManager from '../../api/api-manager';
import { PrevinetAccessToken } from '../../api/generated';
import {
  Composition,
  CompositionType,
  MonitoringResult,
} from '../../api/generated/objectway-zenith';
import {
  formatDateFromForServer,
  formatDateToForServer,
  parseDateToDatestring,
  parseServerShortDate,
} from '../../api/parsers/commons-api-parsers';
import {
  parseDettaglioProdottoToProductDetails,
  parseDettaglioProdottoToProductUnderlying,
  parsePerformanceProdottiToProductsPerformances,
  parsePortafoglioToWallet,
  parsePortafoglioToWalletSummary,
} from '../../api/parsers/wallet-parsers';
import { env } from '../../env';
import {
  DownloaderMediaType,
  FileDownloaded,
  FileDownloadedType,
  ProductDetails,
  ProductPerformanceList,
  Wallet,
  WalletSummary,
} from '../../model';
import { ProductUnderlyingGroupsPaginated } from '../../model/wallet/details/product-underlying-groups-paginated';
import {
  getCategoryForSelectedProduct,
  getDossierFilterCode,
  getDossierFilterContracts,
  getFilenameTransactionsUnderlying,
  labelForWalletMacroCategoryType,
  mapDateTypeToPeriodo,
  mapMacroCategoriesToMacroCategorie,
  MyWalletState,
  PirBoxState,
  WalletError,
  WalletPayloadAction,
} from '../../store';
import { AsyncThunkConfig } from '../../store/async-thunk-config';
import { DossierState, getDossierFilter } from '../../store';
import {
  createWalletPayloadAction,
  getAPIRequestHeadersWithToken,
  getCurrentState,
  getSelectedSection,
  GetThunkAPI,
} from '../../utils/api-utils';
import { WalletRepository } from './wallet-repository';
import { PrevinetServiceType } from '../../hooks';

const createFileDownloaded = (
  filename: string,
  request: { mediaType: DownloaderMediaType; type: FileDownloadedType },
  result: AxiosResponse
): FileDownloaded => ({
  name: filename,
  type: request.type,
  blob: result.data as any,
  mediaType: request.mediaType,
});

const getDownloadFileAPIRequestHeaders = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
  request: { mediaType: DownloaderMediaType; type: FileDownloadedType },
  token?: string
) => ({
  ...getAPIRequestHeadersWithToken(thunkApi, token, {
    Accept: `application/${
      request.mediaType === 'pdf'
        ? request.mediaType
        : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }`,
  }),
  responseType: 'blob',
});

export const transitionWalletRepository: WalletRepository = {
  getWallet: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
  ): Promise<
    WalletPayloadAction<{ wallet: Wallet; summary: WalletSummary }>
  > => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const cointestati =
      thunkApi.getState()?.features?.walletStore?.common?.selectedSection ===
      'SHARED_ACCOUNTS';
    const dossierState: DossierState = thunkApi.getState().data.dossier;
    const dossierFilter = getDossierFilter(
      dossierState.dossierFilter,
      getSelectedSection(thunkApi)
    );
    const result = await apiManager.portafoglioApi.portafoglio(
      {
        contratti: getDossierFilterContracts(dossierFilter),
        dossier: getDossierFilterCode(dossierFilter),
        cointestati,
      },
      getAPIRequestHeadersWithToken(thunkApi, token)
    );
    if (result.data?.elencoPosizioni) {
      const wallet = parsePortafoglioToWallet(result.data);
      const summary = parsePortafoglioToWalletSummary(result.data);
      return createWalletPayloadAction({ wallet, summary }, thunkApi);
    }
    return createWalletPayloadAction({ wallet: null, summary: null }, thunkApi);
  },
  getPerformance: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
  ): Promise<WalletPayloadAction<ProductPerformanceList>> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const dossierState: DossierState = thunkApi.getState().data.dossier;
    const currentState: MyWalletState = getCurrentState(thunkApi);
    const dossierFilter = getDossierFilter(
      dossierState.dossierFilter,
      getSelectedSection(thunkApi)
    );
    const cointestati =
      thunkApi.getState()?.features?.walletStore?.common?.selectedSection ===
      'SHARED_ACCOUNTS';
    const filter = currentState.performanceFilter;
    const result = await apiManager.portafoglioApi.performance(
      mapMacroCategoriesToMacroCategorie(filter.macroCategory),
      {
        dossier: getDossierFilterCode(dossierFilter),
        contratti: getDossierFilterContracts(dossierFilter),
        dal: formatDateFromForServer(
          filter.rangeDate?.from
            ? parseServerShortDate(
                parseDateToDatestring(filter.rangeDate?.from)
              )
            : undefined
        ),
        al: formatDateToForServer(
          filter.rangeDate?.to
            ? parseServerShortDate(parseDateToDatestring(filter.rangeDate?.to))
            : undefined
        ),
        periodo: mapDateTypeToPeriodo(filter.dateType),
        cointestati,
        posizioniChiuse: filter.showClosedPositions || false,
      },
      getAPIRequestHeadersWithToken(thunkApi, token, {
        Accept: 'application/json',
      })
    );
    const performance = parsePerformanceProdottiToProductsPerformances(
      result.data
    );
    return createWalletPayloadAction(performance, thunkApi);
  },
  getTransactions: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
  ): Promise<
    WalletPayloadAction<
      Omit<ProductDetails, 'contract' | 'category' | 'underlyingGroups'>
    >
  > => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const currentState: MyWalletState = getCurrentState(thunkApi);
    const productCode = currentState.selectedProduct?.productCode;
    const contractCode = currentState.selectedProduct?.contract;
    const result = await apiManager.portafoglioApi.movimenti(
      productCode,
      contractCode,
      getAPIRequestHeadersWithToken(thunkApi, token, {
        Accept: 'application/json',
      })
    );
    const details = parseDettaglioProdottoToProductDetails(result.data);
    return createWalletPayloadAction(details, thunkApi);
  },
  getErPortfolioCompositionByType: async (
    type: CompositionType,
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletError>>
  ): Promise<Composition> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const userCode: string = user?.userCode;
    const result =
      await apiManager.portafoglioZenithApi.retrieveRealEconomyPortfolioComposition1(
        type,
        userCode,
        false,
        getAPIRequestHeadersWithToken(thunkApi, token)
      );
    return result.data.result;
  },
  getErEvaluateMonitoring: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletError>>
  ): Promise<MonitoringResult> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const userCode: string = user?.userCode;
    const result = await apiManager.portafoglioZenithApi.evaluateMonitoring1(
      userCode,
      false,
      getAPIRequestHeadersWithToken(thunkApi, token)
    );
    return result.data.result;
  },
  getUnderlying: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>
  ): Promise<WalletPayloadAction<ProductUnderlyingGroupsPaginated>> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const currentState: MyWalletState = getCurrentState(thunkApi);
    const productCode = currentState.selectedProduct?.productCode;
    const contractCode = currentState.selectedProduct?.contract;
    const result = await apiManager.portafoglioApi.sottostanti(
      productCode,
      contractCode,
      getAPIRequestHeadersWithToken(thunkApi, token, {
        Accept: 'application/json',
      })
    );

    const sottostanti = parseDettaglioProdottoToProductUnderlying(result.data);
    return createWalletPayloadAction(sottostanti, thunkApi);
  },
  downloadPerformanceFile: (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
    request: { mediaType: DownloaderMediaType; type: FileDownloadedType }
  ): Promise<FileDownloaded> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const currentState: MyWalletState = getCurrentState(thunkApi);
    const cointestati =
      thunkApi.getState()?.features?.walletStore?.common?.selectedSection ===
      'SHARED_ACCOUNTS';
    const dossierState: DossierState = thunkApi.getState().data.dossier;
    const category = labelForWalletMacroCategoryType(
      currentState.performanceFilter.macroCategory
    );
    const dossierFilter = getDossierFilter(
      dossierState.dossierFilter,
      getSelectedSection(thunkApi)
    );
    const filter = currentState.performanceFilter;
    const filename = `portfolio_${request.type}-${category.defaultMessage
      .toString()
      .toUpperCase()}.${request.mediaType}`;
    return apiManager.portafoglioApi
      .downloadPerformance(
        mapMacroCategoriesToMacroCategorie(filter.macroCategory),
        {
          dossier: getDossierFilterCode(dossierFilter),
          contratti: getDossierFilterContracts(dossierFilter),
          dal: formatDateFromForServer(filter.rangeDate?.from),
          al: formatDateToForServer(filter.rangeDate?.to),
          periodo: mapDateTypeToPeriodo(filter.dateType),
          cointestati,
          posizioniChiuse: filter.showClosedPositions || false,
        },
        getDownloadFileAPIRequestHeaders(thunkApi, request, token)
      )
      .then((result) => createFileDownloaded(filename, request, result));
  },
  downloadHoldingsFile: (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
    request: { mediaType: DownloaderMediaType; type: FileDownloadedType }
  ): Promise<FileDownloaded> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const dossierState: DossierState = thunkApi.getState().data.dossier;
    const cointestati =
      thunkApi.getState()?.features?.walletStore?.common?.selectedSection ===
      'SHARED_ACCOUNTS';
    const dossierFilter = getDossierFilter(
      dossierState.dossierFilter,
      getSelectedSection(thunkApi)
    );
    const filename = `portfolio_${request.type}.${request.mediaType}`;
    return apiManager.portafoglioApi
      .downloadPosizioni(
        { dossier: getDossierFilterCode(dossierFilter), cointestati },
        getDownloadFileAPIRequestHeaders(thunkApi, request, token)
      )
      .then((result) => createFileDownloaded(filename, request, result));
  },
  downloadPirFile: (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
    request: { mediaType: DownloaderMediaType; type: FileDownloadedType }
  ): Promise<FileDownloaded> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const pirboxState: PirBoxState = thunkApi.getState().features.pirbox;
    const filename = `pir_${pirboxState.selectedMandateCode}.${request.mediaType}`;
    return apiManager.portafoglioApi
      .pirBoxDownload(
        pirboxState.selectedMandateCode,
        getDownloadFileAPIRequestHeaders(thunkApi, request, token)
      )
      .then((result) => createFileDownloaded(filename, request, result));
  },
  downloadTransactionsFile: (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
    request: { mediaType: DownloaderMediaType; type: FileDownloadedType }
  ): Promise<FileDownloaded> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const productCategory = getCategoryForSelectedProduct(thunkApi.getState());
    const currentState: MyWalletState = getCurrentState(thunkApi);
    const filename = getFilenameTransactionsUnderlying(
      currentState.productDetails,
      productCategory,
      request
    );

    const productCode = currentState.selectedProduct?.productCode;
    const contractCode = currentState.selectedProduct?.contract;

    return apiManager.portafoglioApi
      .downloadMovimenti(
        productCode,
        contractCode,
        getDownloadFileAPIRequestHeaders(thunkApi, request, token)
      )
      .then((result) => createFileDownloaded(filename, request, result));
  },
  downloadUnderlyingFile: (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
    request: { mediaType: DownloaderMediaType; type: FileDownloadedType }
  ): Promise<FileDownloaded> => {
    const user = thunkApi.getState().data.user;
    const token = user.tokenRubrica || user.token;
    const productCategory = getCategoryForSelectedProduct(thunkApi.getState());
    const currentState: MyWalletState = getCurrentState(thunkApi);
    const filename = getFilenameTransactionsUnderlying(
      currentState.productDetails,
      productCategory,
      request
    );
    const productCode = currentState.selectedProduct?.productCode;
    const contractCode = currentState.selectedProduct?.contract;

    return apiManager.portafoglioApi
      .downloadSottostanti(
        productCode,
        contractCode,
        getDownloadFileAPIRequestHeaders(thunkApi, request, token)
      )
      .then((result) => createFileDownloaded(filename, request, result));
  },
  downloadRealEconomyDetailFile: async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<WalletPayloadAction<WalletError>>>,
    request: { mediaType: DownloaderMediaType; type: FileDownloadedType }
  ): Promise<FileDownloaded> => {
    const dossierState: DossierState = thunkApi.getState().data.dossier;
    const cointestati =
      thunkApi.getState()?.features?.walletStore?.common?.selectedSection ===
      'SHARED_ACCOUNTS';
    const dossierFilter = getDossierFilter(
      dossierState.dossierFilter,
      getSelectedSection(thunkApi)
    );
    const filename = `economia_reale_dettaglio.${request.mediaType}`;

    return apiManager.portafoglioApi
      .downloadDettaglioEconomiaReale(
        {
          dossier: getDossierFilterCode(dossierFilter),
          contratti: getDossierFilterContracts(dossierFilter),
          cointestati,
        },
        getDownloadFileAPIRequestHeaders(thunkApi, request)
      )
      .then((result) => createFileDownloaded(filename, request, result));
  },
  getPrevinetAccessToken: (
    token: string,
    serviceType: PrevinetServiceType
  ): Promise<PrevinetAccessToken> => {
    return apiManager.fondoPensioniApi
      .getPrevinetAccessToken(serviceType, {
        headers: { Authorization: `BEARER ${token}` },
      })
      .then((value) => value.data);
  },
  downloadSchedaProdotto: async (
    token: string,
    codiceISIN: string
  ): Promise<Blob> => {
    if (!codiceISIN) {
      return null;
    }
    try {
      const response: AxiosResponse =
        await apiManager.portafoglioApi.getSchedaProdotto(codiceISIN, {
          headers: {
            Accept: 'application/pdf',
            Authorization: `BEARER ${token}`,
          },
          responseType: 'blob',
        });

      if (response.status === 200 && response.data) {
        return response.data as Blob;
      }

      return null;
    } catch (error: any) {
      return null;
    }
  },
  downloadKidPdf: (documentIsin: string): Promise<Blob> => {
    if (documentIsin) {
      const url = `${env.kidBaseUrl}/${documentIsin}.pdf`;
      return fetch(url)
        .then((x) => (x.status === 200 ? x.blob() : null))
        .catch(() => null);
    }

    return Promise.resolve(null);
  },
};
